// Imports
import {
  CardGrid,
  RowGrid,
  HeroVideo,
  HeroSlideshow,
  Tiles,
  FeaturedTiles,
  StatsBand,
  Divider,
  FeaturedStories,
  Instructions,
  Subscribe,
  TextContent,
  TextWithImage,
  FloatingImageWithText,
  Video,
  Image,
  WhatsOn,
  RollingCarousel,
  HorizontalAdvertisement,
  VerticalAdvertisement,
  EventsGrid,
  FilteredEventsGrid,
  IFrameEmbed,
  Slideshow,
  CodeEmbed,
  DownloadableAssets,
} from "@/components/global/page-builder";
import {
  getGridDataBasedOnCollection,
  getDaysWithWeatherForecast,
  getDaysOfShowInArray,
  getAllShowbags,
  getAllEvents,
  getAllRides,
  getAllFood,
  getShowDates,
  getThemeColors,
  getAllCompetitions,
  getAllCommercialExhibitors,
  getAllNews,
  getSettingsData,
  getIsMapActive,
} from "@/lib/server-side-helpers";

// Render
export async function PageBuilder({ components }) {
  // Only fetch data that's actually needed based on component types
  const componentTypes = components?.map((c) => c.type) || [];
  const componentCollections =
    components?.map((c) => c.collection?.handle).filter(Boolean) || [];

  // More granular data fetching based on actual component needs
  const needsShowbags =
    componentTypes.some((type) => ["card_grid", "row_grid"].includes(type)) &&
    (componentCollections.includes("showbags") ||
      componentCollections.length === 0);
  const needsEvents =
    componentTypes.some((type) =>
      [
        "card_grid",
        "row_grid",
        "events_grid",
        "filtered_events_grid",
        "whats_on",
      ].includes(type)
    ) &&
    (componentCollections.includes("events") ||
      componentTypes.includes("whats_on") ||
      componentTypes.includes("events_grid") ||
      componentTypes.includes("filtered_events_grid"));
  const needsRides =
    componentTypes.some((type) => ["card_grid", "row_grid"].includes(type)) &&
    (componentCollections.includes("rides") ||
      componentCollections.length === 0);
  const needsFood =
    componentTypes.some((type) => ["card_grid", "row_grid"].includes(type)) &&
    (componentCollections.includes("food_outlets") ||
      componentCollections.length === 0);
  const needsCompetitions =
    componentTypes.some((type) => ["card_grid", "row_grid"].includes(type)) &&
    (componentCollections.includes("competitions") ||
      componentCollections.length === 0);
  const needsCommercialExhibitors =
    componentTypes.some((type) => ["card_grid", "row_grid"].includes(type)) &&
    (componentCollections.includes("commercial_exhibitors") ||
      componentCollections.length === 0);
  const needsNews =
    componentTypes.some((type) => ["card_grid", "row_grid"].includes(type)) &&
    (componentCollections.includes("news") ||
      componentCollections.length === 0);
  const needsMapData = componentTypes.some((type) =>
    ["card_grid", "row_grid", "events_grid", "filtered_events_grid"].includes(
      type
    )
  );
  const needsTheme = componentTypes.some((type) =>
    ["tiles", "featured_tiles"].includes(type)
  );
  const needsShowDates = componentTypes.some((type) =>
    [
      "hero_video",
      "hero_slideshow",
      "whats_on",
      "events_grid",
      "filtered_events_grid",
    ].includes(type)
  );

  // Conditionally fetch only what we need
  const [
    showbagsData,
    eventsData,
    ridesData,
    foodData,
    competitionsData,
    commercialExhibitorsData,
    newsData,
    mapData,
    themeData,
    showDatesData,
  ] = await Promise.all([
    needsShowbags ? getAllShowbags() : Promise.resolve({ showbags: [] }),
    needsEvents ? getAllEvents() : Promise.resolve({ allEvents: [] }),
    needsRides ? getAllRides() : Promise.resolve({ rides: [] }),
    needsFood ? getAllFood() : Promise.resolve({ food: [] }),
    needsCompetitions
      ? getAllCompetitions()
      : Promise.resolve({ competitions: [] }),
    needsCommercialExhibitors
      ? getAllCommercialExhibitors()
      : Promise.resolve({ commercialExhibitors: [] }),
    needsNews ? getAllNews() : Promise.resolve({ news: [] }),
    needsMapData ? getIsMapActive() : Promise.resolve({ mapIsActive: false }),
    needsTheme ? getSettingsData() : Promise.resolve({ theme: null }),
    needsShowDates
      ? getShowDates()
      : Promise.resolve({
          daysUntil: 0,
          startDate: "",
          endDate: "",
          showStartDate: null,
          showStartTime: 0,
          unformattedStartDate: "",
          unformattedEndDate: "",
          testMode: false,
          testDate: "",
        }),
  ]);

  // Extract the data
  const { showbags } = showbagsData;
  const { allEvents } = eventsData;
  const { rides } = ridesData;
  const { food } = foodData;
  const { competitions } = competitionsData;
  const { commercialExhibitors } = commercialExhibitorsData;
  const { news } = newsData;
  const { mapIsActive } = mapData;
  const { theme } = themeData;
  const {
    daysUntil,
    startDate,
    endDate,
    showStartDate,
    showStartTime,
    unformattedStartDate,
    unformattedEndDate,
    testMode,
    testDate,
    showCount,
  } = showDatesData;

  const days = getDaysOfShowInArray({
    startDate: unformattedStartDate,
    endDate: unformattedEndDate,
  });

  // const daysWithWeatherForecast = await getDaysWithWeatherForecast(days);
  // // Render
  return (
    <>
      {components?.map((component) => {
        switch (component.type) {
          case "card_grid":
            const {
              gridData,
              categories,
              competitionTags = [],
            } = getGridDataBasedOnCollection(
              showbags,
              allEvents,
              rides,
              food,
              competitions,
              commercialExhibitors,
              news,
              component?.collection?.handle,
              component?.filter_by_location
            );
            if (
              !component?.type ||
              !component?.collection?.handle ||
              !gridData ||
              !categories
            )
              return null;
            return (
              <CardGrid
                key={component.id}
                type={component?.type}
                heading={component?.title}
                collectionHandle={component?.collection?.handle}
                showFilter={component?.show_filter}
                showPagination={component?.show_pagination}
                gridData={gridData}
                categories={categories}
                competitionTags={competitionTags}
                mapIsActive={mapIsActive}
                showDates={{
                  startDate: unformattedStartDate,
                  endDate: unformattedEndDate,
                }}
                show_ads={component?.show_ads}
                ads={component?.ads}
              />
            );

          case "row_grid":
            const {
              gridData: rowGridData,
              categories: rowGridCategories,
              competitionTags: rowGridCompetitionTags = [],
            } = getGridDataBasedOnCollection(
              showbags,
              allEvents,
              rides,
              food,
              competitions,
              commercialExhibitors,
              news,
              component?.collection?.handle
            );
            if (
              !component?.type ||
              !component?.collection?.handle ||
              !rowGridData ||
              !rowGridCategories
            )
              return null;
            return (
              <RowGrid
                key={component.id}
                type={component?.type}
                collectionHandle={component?.collection?.handle}
                showFilter={component?.show_filter}
                showPagination={component?.show_pagination}
                gridData={rowGridData}
                categories={rowGridCategories}
                competitionTags={rowGridCompetitionTags}
                showDates={{
                  startDate: unformattedStartDate,
                  endDate: unformattedEndDate,
                }}
                mapIsActive={mapIsActive}
              />
            );

          case "hero_video":
            if (!component?.type || !component?.video) return null;
            return (
              <HeroVideo
                showCount={showCount}
                key={component.id}
                type={component?.type}
                video={component?.video}
                width={component?.width?.value ?? "2xl"}
                daysUntil={daysUntil}
                startDate={startDate}
                endDate={endDate}
                showStartDate={showStartDate}
                showStartTime={showStartTime}
                unformattedStartDate={unformattedStartDate}
                unformattedEndDate={unformattedEndDate}
              />
            );

          case "hero_slideshow":
            if (!component?.type || component?.slides?.length < 1) return null;
            return (
              <HeroSlideshow
                showCount={showCount}
                key={component.id}
                type={component?.type}
                slides={component?.slides}
                showTextOverlay={component?.show_text_overlay}
                textOverlayAlignment={component?.text_overlay_alignment}
                overlayDate={component?.overlay_date}
                title={component?.title}
                caption={component?.caption}
                buttonTitle={component?.button_title}
                link={component?.link}
                buttonStyle={component?.button_style}
                useCustomColours={component?.use_custom_colours}
                customButtonBackgroundColor={
                  component?.custom_button_background_color
                }
                customButtonForegroundColor={
                  component?.custom_button_foreground_color
                }
                daysUntil={daysUntil}
                startDate={startDate}
                endDate={endDate}
                showStartDate={showStartDate}
                showStartTime={showStartTime}
                unformattedStartDate={unformattedStartDate}
                unformattedEndDate={unformattedEndDate}
                showCtaBar={component?.show_cta_bar}
                ctaBadge={component?.cta_badge}
                ctaTitle={component?.cta_title}
                ctaText={component?.cta_text}
                ctaButtonTitle={component?.cta_button_title}
                ctaLink={component?.cta_link}
              />
            );

          case "tiles":
            if (!component?.type || component?.tiles?.length < 1) return null;
            return (
              <Tiles
                key={component.id}
                type={component?.type}
                tiles={component?.tiles}
                overrideTheme={component?.override_theme_colors}
                theme={theme}
              />
            );

          case "featured_tiles":
            if (!component?.type || component?.tiles?.length < 1) return null;
            return (
              <FeaturedTiles
                key={component.id}
                type={component?.type}
                tiles={component?.tiles}
                sectionTitle={component?.section_title}
                linkLabel={component?.link_label}
                link={component?.link}
                spacingTop={component?.spacing_top}
                spacingBottom={component?.spacing_bottom}
                sectionBackgroundColor={component?.section_background_color}
                sectionForegroundColor={component?.section_foreground_color}
                overrideTheme={component?.override_theme_colors}
              />
            );

          case "stats_band":
            if (!component?.type || component?.items?.length < 1) return null;
            return (
              <StatsBand
                key={component.id}
                type={component?.type}
                items={component?.items}
                spacingTop={component?.spacing_top}
                spacingBottom={component?.spacing_bottom}
              />
            );

          case "divider":
            if (!component?.type) return null;
            return (
              <Divider
                key={component.id}
                type={component?.type}
                spacingTop={component?.spacing_top}
                spacingBottom={component?.spacing_bottom}
              />
            );

          case "featured_stories":
            if (!component?.type || component?.stories?.length < 1) return null;
            return (
              <FeaturedStories
                key={component.id}
                type={component?.type}
                stories={component?.stories}
                sectionTitle={component?.section_title}
                linkLabel={component?.link_label}
                link={component?.link}
                spacingTop={component?.spacing_top}
                spacingBottom={component?.spacing_bottom}
              />
            );

          case "instructions":
            if (!component?.type || component?.steps?.length < 1) return null;
            return (
              <Instructions
                key={component.id}
                type={component?.type}
                title={component?.title}
                steps={component?.steps}
                spacingTop={component?.spacing_top}
                spacingBottom={component?.spacing_bottom}
              />
            );

          case "subscribe":
            if (!component?.type || !component?.title) return null;
            return (
              <Subscribe
                key={component.id}
                type={component?.type}
                title={component?.title}
                description={component?.description}
                backgroundColor={component?.background_color}
                buttonBackgroundColor={component?.button_background_color}
                buttonForegroundColor={component?.button_foreground_color}
                checkPattern={component?.check_pattern}
              />
            );

          case "text_content":
            if (
              !component?.content ||
              !component?.type ||
              !component?.text_alignment
            )
              return null;
            return (
              <TextContent
                key={component.id}
                type={component?.type}
                content={component?.content}
                textAlignment={component?.text_alignment?.value}
                width={component?.width?.value ?? "full"}
              />
            );

          case "text_with_image":
            if (
              !component?.content ||
              !component?.image ||
              !component?.image_alignment ||
              !component?.type
            )
              return null;
            return (
              <TextWithImage
                key={component.id}
                type={component?.type}
                content={component?.content}
                imageAlignment={component?.image_alignment}
                image={component?.image}
                orientation={component?.image?.orientation}
              />
            );

          case "floating_image_with_text":
            if (!component?.floating_image || !component?.content) return null;
            return (
              <FloatingImageWithText
                key={component.id}
                type={component?.type}
                content={component?.content}
                image={component?.floating_image}
                imageAlignment={component?.image_alignment}
                backgroundColor={component?.background_color}
                component={component}
              />
            );

          case "video":
            if (!component?.video && !component?.youtube_video) return null;
            return (
              <Video
                key={component.id}
                type={component?.type}
                videoType={component?.video_type?.value}
                youtubeVideo={component?.youtube_video}
                video={component?.video}
                width={component?.width?.value ?? "2xl"}
                autoplay={component?.autoplay}
              />
            );

          case "image":
            if (!component?.image) return null;
            return (
              <Image
                key={component.id}
                type={component?.type}
                image={component?.image}
                height={component?.height?.value ?? "md"}
                width={component?.width?.value ?? "lg"}
              />
            );

          case "whats_on":
            if (
              !component?.headline ||
              !component?.type ||
              !allEvents ||
              !unformattedStartDate ||
              !unformattedEndDate
            )
              return null;
            return (
              <WhatsOn
                key={component.id}
                type={component?.type}
                events={allEvents}
                headline={component?.headline}
                showDates={{
                  startDate: unformattedStartDate,
                  endDate: unformattedEndDate,
                }}
                testMode={testMode}
                testDate={testDate}
                mapIsActive={mapIsActive}
              />
            );

          case "rolling_carousel":
            if (
              !component?.type ||
              ((!component?.items || component.items.length < 1) &&
                !component?.featured_image)
            ) {
              return null;
            }
            return (
              <RollingCarousel
                key={component.id}
                type={component?.type}
                title={component?.title}
                items={component?.items}
                featuredTitle={component?.featured_title}
                featuredImage={component?.featured_image}
                featuredLink={component?.featured_link}
              />
            );

          case "horizontal_advertisement":
            if (!component?.ad) return null;
            return (
              <HorizontalAdvertisement
                key={component.id}
                type={component?.type}
                ads={component?.ad}
              />
            );

          case "vertical_advertisements":
            if (!component?.ad) return null;
            return (
              <VerticalAdvertisement
                key={component.id}
                type={component?.type}
                ads={component?.ad}
              />
            );

          case "events_grid":
            if (!component?.type || !allEvents) return null;
            return (
              <EventsGrid
                key={component.id}
                collectionHandle="events"
                type={component?.type}
                types={[
                  ...new Set(
                    allEvents?.flatMap((event) =>
                      event?.event_type?.map((type) => type?.title)
                    )
                  ),
                ].sort()}
                events={allEvents}
                showFilter={component?.show_filter}
                showPagination={component?.show_pagination}
                categories={[
                  ...new Set(
                    allEvents?.flatMap((event) =>
                      event?.categories?.map((category) => category?.title)
                    )
                  ),
                ].sort()}
                locations={[
                  ...new Set(
                    allEvents?.flatMap((event) =>
                      event?.locations?.map((location) => location?.title)
                    )
                  ),
                ].sort()}
                days={days}
                showDates={{
                  startDate: unformattedStartDate,
                  endDate: unformattedEndDate,
                }}
                mapIsActive={mapIsActive}
              />
            );

          case "filtered_events_grid":
            if (!component?.type || !allEvents) return null;
            return (
              <FilteredEventsGrid
                key={component.id}
                type={component?.type}
                collectionHandle="events"
                events={allEvents}
                sortOrder={component?.sort_order?.value}
                sortType={component?.sort_type?.value}
                eventTypes={component?.event_types?.flatMap(
                  (type) => type?.slug
                )}
                eventLocations={component?.event_locations.flatMap(
                  (location) => location?.slug
                )}
                eventCategories={component?.event_categories.flatMap(
                  (category) => category?.slug
                )}
                showDates={{
                  startDate: unformattedStartDate,
                  endDate: unformattedEndDate,
                }}
                mapIsActive={mapIsActive}
              />
            );
          case "slideshow":
            if (component?.images?.length < 1) return null;
            return (
              <Slideshow
                key={component.id}
                type={component?.type}
                slides={component?.images}
                width={component?.width?.value}
                height={component?.height?.value}
              />
            );

          case "iframe_embed":
            if (!component?.iframe_source_url) return null;
            return (
              <IFrameEmbed
                key={component.id}
                type={component?.type}
                iframe_source_url={component?.iframe_source_url}
                iframe_height={component?.iframe_height}
              />
            );

          case "code":
            return <CodeEmbed key={component.id} code={component?.code} />;
          case "downloadable_assets":
            return (
              <DownloadableAssets
                key={component.id}
                type={component?.type}
                tabs={component?.tabs}
                heading={component?.heading}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}
